<!--
  Розділ «Умови користування» — публічна оферта закладу.

  Виконує вимогу LiqPay до сайту мерчанта, п.3:
  «описати зрозумілі правила оплати, доставки та повернення коштів; розмістити цю
  інформацію в окремому розділі (наприклад, Оплата і доставка, Повернення) або
  додати до Угоди користувача». Саме її відсутність дала помилку «На сайті
  відсутні умови та правила повернення товарів / послуг» під час перевірки.

  Текст розділів — у спільному з qr-menu utils/termsContent.js.
  Реквізити продавця — з getStart().companySettings. Адресу й телефон закладу
  беремо з tradepoint.contacts і показуємо поряд із юридичними, а не замість
  них: перше — реквізит сторони договору, друге — місце, де гість зараз сидить.
-->
<template>
  <div class="terms">
    <div class="terms__header">
      <button class="back-button" @click="back">
        <div class="cont-button-back-icon">
          <svg
            width="7.492188"
            height="15.40625"
            viewBox="0 0 7.49219 15.4062"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.78 14.7L1.34 9.25C0.48 8.4 0.48 7 1.34 6.14L6.78 0.7"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <p class="back-button-text">Умови користування</p>
      </button>
    </div>

    <div class="terms__content">
      <!-- Розділи — зі спільного termsContent.js, однакового з qr-menu.
           Текст із getStart().terms додається в кінець відповідного розділу. -->
      <section
        v-for="(section, index) in sections"
        :key="section.key"
        class="terms__section"
      >
        <h2 class="terms__title">{{ index + 1 }}. {{ section.title }}</h2>
        <template v-for="(block, i) in section.blocks">
          <p v-if="block.type === 'p'" :key="i" class="terms__text">
            {{ block.text }}
          </p>
          <ul v-else :key="i" class="terms__list">
            <li v-for="(item, j) in block.items" :key="j">
              <!-- Пункт може містити посилання всередині речення -->
              <template v-if="item.parts">
                <template v-for="(part, k) in item.parts">
                  <a
                    v-if="part.href"
                    :key="k"
                    :href="part.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ part.text }}</a
                  >
                  <template v-else>{{ part.text }}</template>
                </template>
              </template>
              <template v-else>{{ item }}</template>
            </li>
          </ul>
        </template>
      </section>

      <!-- Реквізити продавця — вимоги LiqPay п.6 (контакти) і п.7 (адреса),
           а також помилка «немає інформації про відповідального ФОП». -->
      <section v-if="hasSellerInfo" class="terms__section">
        <h2 class="terms__title">
          {{ sections.length + 1 }}. Контакти Закладу
        </h2>
        <dl class="terms__rows">
          <template v-if="sellerName">
            <dt>Продавець</dt>
            <dd>{{ sellerName }}</dd>
          </template>
          <template v-if="showEdrpou && company && company.edrpou">
            <dt>ЄДРПОУ</dt>
            <dd>{{ company.edrpou }}</dd>
          </template>
          <!-- Дві адреси показуємо окремими рядками, і лише коли вони справді
               різні; інакше один рядок із нейтральним «Адреса». -->
          <template v-if="hasBothAddresses">
            <dt>Юридична адреса</dt>
            <dd>{{ legalAddress }}</dd>
            <dt>Адреса закладу</dt>
            <dd>{{ venueAddress }}</dd>
          </template>
          <template v-else-if="sellerAddress">
            <dt>Адреса</dt>
            <dd>{{ sellerAddress }}</dd>
          </template>
          <template v-if="hasBothPhones">
            <dt>Телефон продавця</dt>
            <dd>
              <a :href="'tel:' + legalPhone">{{ legalPhone }}</a>
            </dd>
            <dt>Телефон закладу</dt>
            <dd>
              <a :href="'tel:' + venuePhone">{{ venuePhone }}</a>
            </dd>
          </template>
          <template v-else-if="sellerPhone">
            <dt>Телефон</dt>
            <dd>
              <a :href="'tel:' + sellerPhone">{{ sellerPhone }}</a>
            </dd>
          </template>
          <template v-if="sellerEmail">
            <dt>E-mail</dt>
            <dd>
              <a :href="'mailto:' + sellerEmail">{{ sellerEmail }}</a>
            </dd>
          </template>
        </dl>
      </section>
    </div>

    <!-- Документи платіжної системи та логотип LiqPay (вимога п.9) тримаємо тут,
         а не в футері кожної сторінки: у футері має бути один неяскравий вхід. -->
    <div v-if="showLiqPayLogo" class="terms__legal">
      <LiqPayMark />
    </div>
  </div>
</template>

<script>
import { store } from "@/store.js";
import { navigator } from "./configRoutes";
import {
  getPaymentTermsLink,
  isLiqPay,
  normalizePaymentSystem,
} from "@/utils/paymentTerms";
import LiqPayMark from "./LiqPayMark.vue";
import { buildTermsSections } from "@/utils/termsContent";

// ЄДРПОУ у ФОП часто містить РНОКПП, тобто податковий номер фізособи —
// персональні дані. Вмикати показ лише після підтвердження юриста.
const SHOW_EDRPOU = false;

// Юридична адреса й адреса точки часто збігаються (заклад зареєстрований там,
// де й працює) — тоді другий рядок зайвий. Порівнюємо без регістру й зайвих
// пробілів, бо це два різні поля, які заповнюють руками.
function isSameAddress(a, b) {
  const norm = (v) => String(v).trim().replace(/\s+/g, " ").toLowerCase();
  return norm(a) === norm(b);
}

// Той самий номер у реквізитах і в картці точки записують по-різному
// (+380… і 0…), тому звіряємо лише останні 9 цифр — номер без коду країни.
function isSamePhone(a, b) {
  const digits = (v) => String(v).replace(/\D/g, "").slice(-9);
  return digits(a) === digits(b);
}

export default {
  name: "Terms",
  props: [
    "contacts",
    "phoneNumber",
    "isActiveMenu",
    "isActivewaiter",
    "isWithoutPay",
  ],
  components: { LiqPayMark },
  data() {
    return {
      showEdrpou: SHOW_EDRPOU,
    };
  },
  methods: {
    // Якщо заклад вимкнув і меню, і виклик офіціанта, стартової сторінки для
    // нього не існує — tpay.vue у такому разі одразу відкриває список рахунків.
    // Повертати туди ж, інакше гість потрапить на порожній екран.
    back() {
      const hasStartPage = Boolean(this.isActiveMenu || this.isActivewaiter);
      const target = hasStartPage ? navigator.START_PAGE : navigator.ORDERS;
      this.$emit("changePage", target);

      const url = new URL(window.location.href);
      if (hasStartPage) url.searchParams.delete("skyservice");
      else url.searchParams.set("skyservice", "pay");
      window.history.pushState(null, "", url.toString());
    },
  },
  computed: {
    company() {
      return store.company;
    },
    // Заклад може працювати без онлайн-оплати (withoutPay) або без налаштованої
    // платіжки — тоді в оферті не має бути тверджень про оплату карткою.
    hasOnlinePayment() {
      return (
        !this.isWithoutPay &&
        Boolean(normalizePaymentSystem(store.activePaymentSystem))
      );
    },
    showLiqPayLogo() {
      return isLiqPay(store.activePaymentSystem);
    },
    sections() {
      return buildTermsSections({
        sellerName: this.sellerName,
        paymentSystemName: this.paymentSystemName,
        paymentTermsUrl: this.paymentTermsLink,
        isLiqPay: isLiqPay(store.activePaymentSystem),
        hasOnlinePayment: this.hasOnlinePayment,
        terms: store.terms,
      });
    },
    // Платіжку беремо з getStart, а не з сокета: на цю сторінку заходять
    // і без відкритого рахунку, тож paymentType там порожній.
    paymentTermsLink() {
      return getPaymentTermsLink(store.activePaymentSystem);
    },
    paymentSystemName() {
      switch (normalizePaymentSystem(store.activePaymentSystem)) {
        case "liqpay":
          return "LiqPay";
        case "portmone":
          return "Portmone";
        default:
          return "";
      }
    },
    // legalType: 1 — ФОП, тоді значуще ПІБ; 0 — юрособа, тоді найменування.
    // companySettings.companyName — це юридична назва, вона відрізняється від
    // кореневого companyName, який показуємо в шапці як назву закладу.
    sellerName() {
      const company = this.company;
      if (company) {
        const legalName = company.companyName || "";
        const person = company.fullName || "";
        const name =
          company.legalType === 1 ? person || legalName : legalName || person;
        if (name) return name;
      }
      return store.companyName || "";
    },
    // LiqPay у п.7 вимагає адресу продавця — сторони договору, тобто юридичну.
    // Але ховати за нею адресу торгової точки не можна: у ФОПа legalAddress
    // часто адреса реєстрації, і гість не впізнає в ній заклад, у якому сидить.
    legalAddress() {
      return (this.company && this.company.legalAddress) || "";
    },
    // Приходить у props уже склеєною через utils/address.js — з містом, бо в
    // реквізитах адреса має бути повною: сторінку відкривають і прямим лінком,
    // а не лише з-за столу, і за цією адресою гість надсилає претензію.
    venueAddress() {
      return this.contacts || "";
    },
    hasBothAddresses() {
      return Boolean(
        this.legalAddress &&
          this.venueAddress &&
          !isSameAddress(this.legalAddress, this.venueAddress)
      );
    },
    // Коли адреса одна (або друга її дублює) — показуємо без уточнення, чия вона.
    sellerAddress() {
      return this.legalAddress || this.venueAddress;
    },
    // З телефоном те саме: у реквізитах може бути бухгалтерія, а гість дзвонить
    // у сам заклад.
    legalPhone() {
      return (this.company && this.company.phone) || "";
    },
    venuePhone() {
      return this.phoneNumber || "";
    },
    hasBothPhones() {
      return Boolean(
        this.legalPhone &&
          this.venuePhone &&
          !isSamePhone(this.legalPhone, this.venuePhone)
      );
    },
    sellerPhone() {
      return this.legalPhone || this.venuePhone;
    },
    sellerEmail() {
      return (this.company && this.company.email) || "";
    },
    hasSellerInfo() {
      return Boolean(
        this.sellerName ||
          this.sellerAddress ||
          this.sellerPhone ||
          this.sellerEmail
      );
    },
  },
};
</script>

<style scoped>
/* Кольори беремо лише зі змінних теми (styleWeB), інакше сторінка випадає
   з оформлення закладу — на темних темах була б біла картка з темним текстом. */
.terms {
  min-height: 100vh;
  padding: 5px 20px 20px 20px;
  background: var(--app-bg) !important;
  color: var(--text-head) !important;
  display: flex;
  flex-direction: column;
}

.terms__header {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 15px;
}

.back-button {
  padding: 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  /* Відступ між стрілкою і текстом — саме gap, а не margin на тексті:
     текст це <p>, а в tpay.vue глобально стоїть `p { margin: 0 !important }`. */
  gap: 10px;
  border: none;
  background: var(--app-bg) !important;
}

.cont-button-back-icon {
  height: 45px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--app-bg) !important;
  color: var(--text-head) !important;
}

.back-button-text {
  font-family: "Manrope", serif;
  font-size: 23px;
  font-weight: 800;
  color: var(--text-head) !important;
}

/* Без картки: текст лежить прямо на фоні сторінки. Колір беремо --text-head —
   той самий, що в заголовка сторінки. --text-innerBody призначений для тексту
   на картці, а --text-content у частині тем темний і на темних темах зливався
   з фоном: сторінка ставала нечитабельною. */
.terms__content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 0 8px;
  color: var(--text-head) !important;
}

.terms__title {
  font-family: "Manrope", serif;
  font-size: 17px;
  font-weight: 700;
  /* відступ дає gap у .terms__section */
  margin: 0 0 2px;
  color: var(--text-head) !important;
}

.terms__list {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Відступи між абзацами робимо через gap, а не margin: у tpay.vue глобально
   стоїть `p { margin: 0 !important }`, яке перебило б будь-який margin. */
.terms__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.terms__list li,
.terms__text {
  font-size: 14px;
  line-height: 1.45;
  color: var(--text-head) !important;
}


.terms__rows {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 14px;
  margin: 0;
  font-size: 14px;
}

.terms__rows dt {
  color: var(--text-head) !important;
  opacity: 0.6;
  font-weight: 500;
}

.terms__rows dd {
  margin: 0;
  color: var(--text-head) !important;
  word-break: break-word;
}

.terms__rows a {
  color: var(--text-head) !important;
  text-decoration: underline;
}

.terms__legal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 10px 4px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.terms__list a {
  color: var(--text-head) !important;
  text-decoration: underline;
}

</style>
